import type { FC } from 'react';
import React, { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import { createAddUrlParams } from '../../utils/addUrlParams';
import { FAStateContext } from '../../context/FAStateContext';
import { ASPECT_RATIO_LARGE, ASPECT_RATIO_MEDIUM, ASPECT_RATIO_SMALL } from '../Slider';
import { updateAltWithI18n } from '../../utils/imgI18nAlt';

const Slide = styled.div`
  aspect-ratio: ${() => ASPECT_RATIO_SMALL};
  position: relative;
  min-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (min-width: ${(props) => props.theme.breakpoints.md}px) {
    aspect-ratio: ${() => ASPECT_RATIO_MEDIUM};
    width: 100%;
    overflow: hidden;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.lg}px) {
    aspect-ratio: ${() => ASPECT_RATIO_LARGE};
  }
`;

interface StyledImageProps {
  readonly active?: boolean;
}

const StyledImage = styled.img<StyledImageProps>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (min-width: ${(props) => props.theme.breakpoints.lg}px) {
    height: auto;
  }
`;

export interface ImageProps {
  readonly url: string;
  readonly index: number;
  readonly altText: string;
}

export const Image: FC<ImageProps> = ({ url, index, altText }) => {
  const {
    carline: { name },
    imageIndexState: [imageIndex],
  } = useContext(FAStateContext);

  const addUrlParams = createAddUrlParams(url);
  const theme = useTheme();
  const alt = `${name} ${updateAltWithI18n(altText) || index + 1}`;
  const isActive = index === imageIndex;

  return (
    <Slide aria-hidden={!isActive}>
      <StyledImage
        src={url}
        srcSet={[483, 555, 768, 858, 966, 1110, 1280, 1440, 1536, 1920]
          .map((width) => `${addUrlParams(width)} ${width}w`)
          .join(', ')}
        sizes={`(min-width: ${theme.breakpoints.xxl}px) ${theme.breakpoints.xxl}px, (min-width: ${theme.breakpoints.md}px) 100vw, 134vw`}
        active={index === imageIndex}
        alt={alt}
        loading="lazy"
      />
    </Slide>
  );
};
